import { useCallback, type Dispatch, type SetStateAction } from 'react';
import {
  createApiClient,
  type PublicConversation,
  type PublicMessage,
  type PublicIdentity,
  type SendMessageParams,
  type EditMessageParams,
  type SerializedWrappedKey,
} from '@adieuu/shared';
import { markFirstMessageSent } from '../../constants/onboarding';
import { isPeerTrustBlockedError } from '../../services/peerTrust';
import { applyThreadSummary } from '../../services/threadSummary';
import { emitConversationThreadEvent } from '../../services/conversationThreadEvents';
import {
  encryptMessage,
  encryptGroupName,
  type RecipientKeys,
} from '../../services/conversationCryptoService';
import type {
  ConversationMessagesState,
  DecryptedConversation,
  DisplayMessage,
  SendMessageErrorResult,
} from './types';

type ApiClient = ReturnType<typeof createApiClient>;

/** Apply a topic on create only when the returned DM does not already have a name. */
export function shouldApplyCreateDmTopic(
  conversation: Pick<PublicConversation, 'encryptedName'>,
): boolean {
  return !conversation.encryptedName;
}

export interface ConversationCreateAndSendParams {
  isLoggedIn: boolean;
  identity: PublicIdentity | null;
  api: ApiClient;
  conversations: DecryptedConversation[];
  getSigningKey: () => Uint8Array | null | undefined;
  getPqSigningKey: () => Uint8Array | null;
  fetchRecipientKeys: (
    conversationId: string,
    participantIds: string[],
    useForwardSecrecy?: boolean,
    signal?: AbortSignal
  ) => Promise<RecipientKeys[]>;
  toDecrypted: (conv: PublicConversation) => DecryptedConversation;
  resolveParticipants: (ids: string[]) => Promise<Record<string, PublicIdentity>>;
  setConversations: Dispatch<SetStateAction<DecryptedConversation[]>>;
  setMessagesState: Dispatch<SetStateAction<Record<string, ConversationMessagesState>>>;
  setSending: Dispatch<SetStateAction<boolean>>;
  accountSubjectId?: string;
  prepareConversationMetadata: (
    conversation: PublicConversation
  ) => Promise<{ metadataKey: Uint8Array; wrappedMetadataKeys: SerializedWrappedKey[] } | null>;
  syncConversationMetadata: (conversationId: string) => Promise<void>;
  persistEncryptedReadState: (conversationId: string, messageId: string) => Promise<void>;
}

/**
 * Create DM/group conversations and send encrypted text messages.
 */
export function useConversationCreateAndSend(params: ConversationCreateAndSendParams) {
  const {
    isLoggedIn,
    identity,
    api,
    conversations,
    getSigningKey,
    getPqSigningKey,
    fetchRecipientKeys,
    toDecrypted,
    resolveParticipants,
    setConversations,
    setMessagesState,
    setSending,
    accountSubjectId,
    prepareConversationMetadata,
    syncConversationMetadata,
    persistEncryptedReadState,
  } = params;

  const createDM = useCallback(
    async (
      participantId: string,
      options?: { forceNew?: boolean; topic?: string }
    ): Promise<PublicConversation | null> => {
      try {
        const { forceNew, topic } = options ?? {};
        const resp = await api.conversations.create({
          type: 'dm',
          participants: [participantId],
          forceNew: forceNew === true ? true : undefined,
        });
        if (!resp.data) return null;

        let conv: PublicConversation = resp.data;
        const trimmedTopic = topic?.trim();
        if (trimmedTopic && shouldApplyCreateDmTopic(conv)) {
          const prepared = await prepareConversationMetadata(conv);
          if (prepared) {
            const encrypted = encryptGroupName(trimmedTopic, conv.id, prepared.metadataKey);
            const nameResp = await api.conversations.updateName(
              conv.id,
              encrypted.encryptedName,
              encrypted.nameNonce,
              prepared.wrappedMetadataKeys,
              undefined,
              { ciphertextRotation: true }
            );
            if (nameResp.data) {
              conv = {
                ...nameResp.data,
                encryptedName: nameResp.data.encryptedName ?? encrypted.encryptedName,
                nameNonce: nameResp.data.nameNonce ?? encrypted.nameNonce,
                wrappedMetadataKeys:
                  nameResp.data.wrappedMetadataKeys ?? prepared.wrappedMetadataKeys,
              };
            } else {
              conv = {
                ...conv,
                encryptedName: encrypted.encryptedName,
                nameNonce: encrypted.nameNonce,
                wrappedMetadataKeys: prepared.wrappedMetadataKeys,
              };
            }
          }
        }

        const decrypted = toDecrypted(conv);
        setConversations((prev) => {
          if (prev.some((c) => c.id === decrypted.id)) return prev;
          return [decrypted, ...prev];
        });
        void resolveParticipants(decrypted.participants);
        return conv;
      } catch {
        // Error
      }
      return null;
    },
    [api, toDecrypted, resolveParticipants, prepareConversationMetadata]
  );

  const createGroup = useCallback(
    async (
      participantIds: string[],
      conversationTopicOrName?: string
    ): Promise<PublicConversation | null> => {
      try {
        let encryptedName: string | undefined;
        let nameNonce: string | undefined;
        let wrappedMetadataKeys: SerializedWrappedKey[] | undefined;

        const resp = await api.conversations.create({
          type: 'group',
          participants: participantIds,
        });

        if (resp.data && conversationTopicOrName) {
          const prepared = await prepareConversationMetadata(resp.data);
          if (prepared) {
            const encrypted = encryptGroupName(
              conversationTopicOrName,
              resp.data.id,
              prepared.metadataKey
            );
            await api.conversations.updateName(
              resp.data.id,
              encrypted.encryptedName,
              encrypted.nameNonce,
              prepared.wrappedMetadataKeys,
              undefined,
              { ciphertextRotation: true }
            );
            encryptedName = encrypted.encryptedName;
            nameNonce = encrypted.nameNonce;
            wrappedMetadataKeys = prepared.wrappedMetadataKeys;
          }
        }

        if (resp.data) {
          const conv = {
            ...resp.data,
            encryptedName,
            nameNonce,
            ...(wrappedMetadataKeys ? { wrappedMetadataKeys } : {}),
          };
          const decrypted = toDecrypted(conv);
          setConversations((prev) => [decrypted, ...prev]);
          void resolveParticipants(decrypted.participants);
          return conv;
        }
      } catch {
        // Error
      }
      return null;
    },
    [api, toDecrypted, resolveParticipants, prepareConversationMetadata]
  );

  const sendTextMessage = useCallback(
    async (
      conversationId: string,
      plaintext: string,
      options?: {
        expiresInSeconds?: number;
        useForwardSecrecy?: boolean;
        replyToMessageId?: string;
        e2eMediaIds?: string[];
        moderationEnabled?: boolean;
        mentionedIdentityIds?: string[];
        threadRootId?: string;
        alsoSendToConversation?: boolean;
        skipMessageStateUpdate?: boolean;
        /** When true, do not toggle global `sending` (e.g. background media outbox). */
        suppressGlobalSending?: boolean;
        signal?: AbortSignal;
        /** Caller-chosen id (optimistic sends match the server copy on it). */
        clientMessageId?: string;
      }
    ): Promise<PublicMessage | SendMessageErrorResult | null> => {
      if (!isLoggedIn || !identity) return null;

      const conversation = conversations.find((c) => c.id === conversationId);
      if (!conversation) return null;

      const useFs = options?.useForwardSecrecy ?? false;
      const expiresInSeconds = options?.expiresInSeconds;
      const signal = options?.signal;

      const throwIfAborted = () => {
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      };

      const manageSending = options?.suppressGlobalSending !== true;
      if (manageSending) setSending(true);
      try {
        throwIfAborted();
        const signingKey = getSigningKey();
        if (!signingKey) throw new Error('No signing key available');

        const recipients = await fetchRecipientKeys(
          conversationId,
          conversation.participants,
          useFs,
          signal
        );
        throwIfAborted();
        if (recipients.length === 0) throw new Error('No recipient keys available');

        const clientMessageId = options?.clientMessageId ?? crypto.randomUUID();
        const encrypted = encryptMessage(
          plaintext,
          recipients,
          signingKey,
          { conversationId, fromIdentityId: identity.id, clientMessageId },
          'default',
          {
            forwardSecrecyRequested: useFs,
            pqSigningPrivateKey: getPqSigningKey() ?? undefined,
          }
        );
        throwIfAborted();

        const fsApplied = encrypted.wrappedKeys.some((k) => k.preKeyType !== 'static');
        const fsDowngraded = useFs && encrypted.fsDowngradedDeviceIds.length > 0;

        const sendParams: SendMessageParams = {
          ciphertext: encrypted.ciphertext,
          nonce: encrypted.nonce,
          wrappedKeys: encrypted.wrappedKeys,
          signature: encrypted.signature,
          cryptoProfile: encrypted.cryptoProfile,
          clientMessageId,
          expiresInSeconds,
          ...(options?.replyToMessageId ? { replyToMessageId: options.replyToMessageId } : {}),
          ...(options?.e2eMediaIds?.length ? { e2eMediaIds: options.e2eMediaIds } : {}),
          ...(options?.moderationEnabled != null ? { moderationEnabled: options.moderationEnabled } : {}),
          ...(options?.mentionedIdentityIds?.length ? { mentionedIdentityIds: options.mentionedIdentityIds } : {}),
          ...(options?.threadRootId
            ? {
                threadRootId: options.threadRootId,
                ...(options.alsoSendToConversation ? { alsoSendToConversation: true } : {}),
              }
            : {}),
        };

        const resp = await api.conversations.sendMessage(
          conversationId,
          sendParams,
          signal ? { signal } : undefined
        );
        throwIfAborted();

        if (!resp.success && signal?.aborted) {
          throw new DOMException('Aborted', 'AbortError');
        }

        if (resp.data) {
          const { threadRootSummary, ...sentMessage } = resp.data;
          const threadOnly = sentMessage.threadOnly === true;
          const displayMsg: DisplayMessage = {
            ...sentMessage,
            decryptedContent: plaintext,
            signatureVerified: true,
            // Reflect what actually happened, not what was requested: if all
            // devices fell back to static wrapping, this message has no FS.
            forwardSecrecy: useFs && fsApplied,
            ...(fsDowngraded ? { fsDowngraded: true } : {}),
          };

          if (!options?.skipMessageStateUpdate && !threadOnly) {
            setMessagesState((prev) => ({
              ...prev,
              [conversationId]: {
                ...(prev[conversationId] ?? {
                  messages: [],
                  olderCursor: null,
                  newerPaginationAfterId: null,
                  hasNewerPages: false,
                  loading: false,
                  showManualLoadOlder: false,
                  showManualLoadNewer: false,
                }),
                messages: [displayMsg, ...(prev[conversationId]?.messages ?? [])],
                newerPaginationAfterId: displayMsg.id,
                hasNewerPages: false,
              },
            }));
          }

          if (displayMsg.threadRootId) {
            const rootId = displayMsg.threadRootId;
            if (threadRootSummary !== undefined) {
              setMessagesState((prev) => {
                const state = prev[conversationId];
                const root = state?.messages.find((m) => m.id === rootId);
                if (!state || !root) return prev;
                const next = applyThreadSummary(root, threadRootSummary);
                if (next === root) return prev;
                return {
                  ...prev,
                  [conversationId]: { ...state, messages: state.messages.map((m) => (m.id === rootId ? next : m)) },
                };
              });
              emitConversationThreadEvent({ kind: 'summary', conversationId, rootId, summary: threadRootSummary });
            }
            emitConversationThreadEvent({ kind: 'own_reply', conversationId, message: displayMsg });
          }

          // A thread-only reply is not in the stream, so it is neither the preview nor a read point.
          if (!threadOnly) {
            setConversations((prev) =>
              prev.map((c) =>
                c.id === conversationId
                  ? { ...c, lastMessageAt: sentMessage.createdAt, lastMessageId: sentMessage.id }
                  : c
              ).sort((a, b) => {
                const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
                const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
                return bTime - aTime;
              })
            );
          }

          markFirstMessageSent(accountSubjectId);

          if (!threadOnly) void persistEncryptedReadState(conversationId, sentMessage.id);
          void syncConversationMetadata(conversationId);

          return sentMessage;
        }

        if (resp.error?.code === 'FORBIDDEN') {
          return { errorCode: 'BLOCKED' };
        }
      } catch (err) {
        if (isPeerTrustBlockedError(err)) {
          return { errorCode: 'PEER_TRUST_BLOCKED' };
        }
        if (err instanceof DOMException && err.name === 'AbortError') {
          throw err;
        }
        if (signal?.aborted) {
          throw new DOMException('Aborted', 'AbortError');
        }
        console.error('[Conversations] Failed to send message:', err);
      } finally {
        if (manageSending) setSending(false);
      }
      return null;
    },
    [isLoggedIn, identity, conversations, getSigningKey, getPqSigningKey, fetchRecipientKeys, api, persistEncryptedReadState, syncConversationMetadata]
  );

  const editTextMessage = useCallback(
    async (
      conversationId: string,
      messageId: string,
      plaintext: string,
      options?: {
        useForwardSecrecy?: boolean;
        e2eMediaIds?: string[];
        signal?: AbortSignal;
        /**
         * The original message's clientMessageId, bound into the v2 signature
         * (stable across edits). When omitted, it is resolved with an extra
         * GET request.
         */
        clientMessageId?: string;
      }
    ): Promise<PublicMessage | SendMessageErrorResult | null> => {
      if (!isLoggedIn || !identity) return null;

      const conversation = conversations.find((c) => c.id === conversationId);
      if (!conversation) return null;

      const useFs = options?.useForwardSecrecy ?? false;
      const signal = options?.signal;

      const throwIfAborted = () => {
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      };

      setSending(true);
      try {
        throwIfAborted();
        const signingKey = getSigningKey();
        if (!signingKey) throw new Error('No signing key available');

        let clientMessageId = options?.clientMessageId;
        if (!clientMessageId) {
          const msgResp = await api.conversations.getMessage(conversationId, messageId);
          clientMessageId = msgResp.data?.clientMessageId;
          if (!clientMessageId) {
            throw new Error('Unable to resolve clientMessageId for edit signature');
          }
        }
        throwIfAborted();

        const recipients = await fetchRecipientKeys(
          conversationId,
          conversation.participants,
          useFs,
          signal
        );
        throwIfAborted();
        if (recipients.length === 0) throw new Error('No recipient keys available');

        const encrypted = encryptMessage(
          plaintext,
          recipients,
          signingKey,
          { conversationId, fromIdentityId: identity.id, clientMessageId },
          'default',
          {
            forwardSecrecyRequested: useFs,
            pqSigningPrivateKey: getPqSigningKey() ?? undefined,
          }
        );
        throwIfAborted();

        const fsApplied = encrypted.wrappedKeys.some((k) => k.preKeyType !== 'static');
        const fsDowngraded = useFs && encrypted.fsDowngradedDeviceIds.length > 0;

        const clientEditId = crypto.randomUUID();
        const editParams: EditMessageParams = {
          ciphertext: encrypted.ciphertext,
          nonce: encrypted.nonce,
          wrappedKeys: encrypted.wrappedKeys,
          signature: encrypted.signature,
          cryptoProfile: encrypted.cryptoProfile,
          clientEditId,
          ...(options?.e2eMediaIds?.length ? { e2eMediaIds: options.e2eMediaIds } : {}),
        };

        const resp = await api.conversations.editMessage(
          conversationId,
          messageId,
          editParams,
          signal ? { signal } : undefined
        );
        throwIfAborted();

        if (!resp.success && signal?.aborted) {
          throw new DOMException('Aborted', 'AbortError');
        }

        if (resp.data) {
          const displayMsg: DisplayMessage = {
            ...resp.data,
            decryptedContent: plaintext,
            signatureVerified: true,
            forwardSecrecy: useFs && fsApplied,
            ...(fsDowngraded ? { fsDowngraded: true } : {}),
          };

          setMessagesState((prev) => {
            const cur = prev[conversationId];
            if (!cur) return prev;
            return {
              ...prev,
              [conversationId]: {
                ...cur,
                messages: cur.messages.map((m) => (m.id === messageId ? displayMsg : m)),
              },
            };
          });
          // Thread-only replies live outside the buffer above; open panels refetch.
          emitConversationThreadEvent({ kind: 'edited', conversationId, messageId });

          return resp.data;
        }

        if (resp.error?.code === 'MAX_EDITS_REACHED') {
          return { errorCode: 'MAX_EDITS_REACHED' };
        }
        if (resp.error?.code === 'FORBIDDEN') {
          return { errorCode: 'BLOCKED' };
        }
      } catch (err) {
        if (isPeerTrustBlockedError(err)) {
          return { errorCode: 'PEER_TRUST_BLOCKED' };
        }
        if (err instanceof DOMException && err.name === 'AbortError') {
          throw err;
        }
        if (signal?.aborted) {
          throw new DOMException('Aborted', 'AbortError');
        }
        console.error('[Conversations] Failed to edit message:', err);
      } finally {
        setSending(false);
      }
      return null;
    },
    [isLoggedIn, identity, conversations, getSigningKey, getPqSigningKey, fetchRecipientKeys, api, setMessagesState]
  );

  return { createDM, createGroup, sendTextMessage, editTextMessage };
}
